import { Link, useParams } from 'react-router-dom';
import { library } from '../lib/api';
import { prelearnLinks } from '../lib/prelearn';
import { useApi } from '../hooks/useApi';
import { useTranslation } from '../i18n';
import { Sidenote } from '../components/ui';

export default function BookDetailPage() {
  const { t } = useTranslation();
  const { bookId = '' } = useParams();
  const state = useApi(() => library.preview(bookId), [bookId]);

  if (state.status === 'loading') {
    return (
      <div className="container" aria-busy="true" aria-live="polite">
        <section className="book-hero" aria-hidden="true">
          <div>
            <div className="cover-wrap">
              <div className="cover-lg skeleton" />
            </div>
            <div className="shelf-lg" />
          </div>
          <div className="book-info">
            <div className="skeleton skeleton-line w-30" style={{ height: 11 }} />
            <div className="skeleton skeleton-line w-80 mt-16" style={{ height: 48 }} />
            <div className="skeleton skeleton-line w-40 mt-8" style={{ height: 22 }} />
            <div className="skeleton skeleton-line w-90 mt-16" />
            <div className="skeleton skeleton-line w-80 mt-2" />
            <div className="skeleton skeleton-line w-70 mt-2" />
          </div>
          <aside className="book-hero__aside">
            <div className="skeleton skeleton-line w-30" style={{ height: 11 }} />
            <div className="skeleton skeleton-line w-60 mt-2" />
            <div className="skeleton skeleton-line w-50 mt-2" />
          </aside>
        </section>
        <span className="visually-hidden">{t('common.loading')}</span>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="container">
        <p className="muted">{t('detail.bookMissing')}</p>
        <Link to="/library">{t('common.back')}</Link>
      </div>
    );
  }
  const book = state.data.book;
  const map_excerpt = state.data.map_excerpt ?? {};
  const sample_questions = state.data.sample_questions ?? [];
  if (!book) {
    return <div className="container"><p className="muted">{t('detail.bookMissing')}</p></div>;
  }

  return (
    <div className="container">
      <div className="crumb">
        <Link to="/library">{t('nav.library')}</Link>
        <span className="sep">/</span>
        <span className="current">{book.title}</span>
      </div>

      <section className="book-hero">
        <div>
          <div className="cover-wrap">
            <div className="cover-lg">
              <img src={`/covers/${slugify(book.title)}.jpg`} alt={`${book.title} cover`}
                   onError={(e) => (e.currentTarget.style.display = 'none')} />
            </div>
            <div className="page-edge-lg" />
          </div>
          <div className="shelf-lg" />
        </div>

        <div className="book-info">
          <div className="kicker book-meta-kicker">
            {t(`source.${book.source}` as const)}
            {t('detail.metaSeparator')}
            {book.page_count} {t('detail.pages')}
            {t('detail.metaSeparator')}
            {book.language}
          </div>
          <h1 className="book-detail__title">{book.title}</h1>
          <div className="by"><em>{t('detail.byAuthor')} {book.author ?? '—'}</em></div>

          <p className="book-desc">
            {(map_excerpt as { overview?: string })?.overview ??
              t('detail.previewNodes')}
          </p>

          <div className="cta-row">
            <Link to={`/books/${book.id}/read`} className="btn btn-accent">
              {t('common.beginReading')} <span className="arrow">→</span>
            </Link>
            <Link to={`/books/${book.id}/map`} className="btn btn-secondary">
              {t('common.openMindmap')}
            </Link>
            <Link to={`/books/${book.id}/quiz`} className="btn btn-ghost">
              {t('common.startAssessment')}
            </Link>
            <Link to={`/books/${book.id}/propose`} className="btn btn-ghost">
              {t('detail.propose')}
            </Link>
            <Link to={`/books/${book.id}/report`} className="btn btn-ghost">
              {t('detail.report')}
            </Link>
          </div>
        </div>

        <aside className="book-hero__aside">
          <Sidenote label={t('detail.sourceLabel')}>
            <p>{t(`source.${book.source}` as const)}</p>
            <p className="mono">{book.language} · {book.page_count} {t('detail.pages')}</p>
          </Sidenote>

          {(map_excerpt as { quote?: string })?.quote && (
            <Sidenote label={t('detail.firstQuoteLabel')} tone="accent" marker="“">
              <p>{(map_excerpt as { quote?: string }).quote}</p>
            </Sidenote>
          )}

          <Sidenote label={t('detail.relatedCredentialLabel')} marker="★">
            <p>{t('detail.relatedCredentialBody')}</p>
          </Sidenote>
        </aside>
      </section>

      <section className="prelearn-section">
        <div className="sec-head">
          <h2>{t('prelearn.title')}</h2>
          <span className="meta">{t('prelearn.subtitle')}</span>
        </div>
        {(['books', 'media'] as const).map((group) => (
          <div key={group} className="prelearn-group">
            <div className="prelearn-group__label">{t(`prelearn.${group}` as const)}</div>
            <div className="prelearn-links">
              {prelearnLinks(book.title, book.author).filter((l) => l.group === group).map((l) => (
                <a key={l.key} href={l.url} target="_blank" rel="noopener noreferrer"
                   className="prelearn-link">{l.label} <span className="prelearn-link__ext">↗</span></a>
              ))}
            </div>
          </div>
        ))}
        <p className="prelearn-note">{t('prelearn.note')}</p>
      </section>

      <section className="book-sample-section">
        <div className="sec-head"><h2>{t('detail.sampledQuestions')}</h2><span className="meta">3 / —</span></div>
        {sample_questions.length === 0 && (
          <p className="muted">{t('detail.noQuestions')}</p>
        )}
        <div className="q-list">
          {sample_questions.slice(0, 3).map((q: unknown, i: number) => {
            const qq = q as { id: string; tier: string; stem: string; ccss_code?: string; dok_level?: number; choices?: Array<{ label: string; text: string }> };
            return (
              <article key={qq.id ?? i} className="q-item">
                <div className="q-meta">
                  <span className={`q-tier q-tier-${qq.tier?.toLowerCase()}`}>{qq.tier}</span>
                  {qq.ccss_code && <span className="chip chip-mute">{qq.ccss_code}</span>}
                  {qq.dok_level != null && <span className="chip chip-mute">DOK {qq.dok_level}</span>}
                </div>
                <p className="q-stem">{qq.stem}</p>
                <div className="q-choices">
                  {qq.choices?.map((c) => (
                    <div key={c.label} className="q-choice">
                      <span className="q-choice-letter">{c.label}</span>
                      <span className="q-choice-text">{c.text}</span>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
